import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, SaveIcon } from 'lucide-react';
import { BillItem } from '../types/Bill';
export function BillForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const billId = searchParams.get('id');
  const isEditMode = !!billId;
  const [formData, setFormData] = useState({
    billNumber: isEditMode ? 'BILL-2024-001' : '',
    supplierId: isEditMode ? '1' : '',
    supplierName: isEditMode ? 'Premium Tea Estates Ltd' : '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as const
  });
  const [items, setItems] = useState<BillItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  // Mock suppliers for dropdown
  const suppliers = [{
    id: '1',
    name: 'Premium Tea Estates Ltd'
  }, {
    id: '2',
    name: 'Spice Traders Co'
  }, {
    id: '3',
    name: 'Packaging Solutions'
  }, {
    id: '4',
    name: 'Organic Herbs Ltd'
  }];
  const calculateItemTotal = (quantity: number, unitPrice: number) => {
    return quantity * unitPrice;
  };
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };
  const addItem = () => {
    const newItem: BillItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof BillItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving bill:', {
      formData,
      items
    });
    navigate('/bills');
  };
  const handleSaveAsDraft = () => {
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/bills');
  };
  return <div className="space-y-6" data-id="element-4311">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4312">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4313">
          <ArrowLeftIcon size={20} data-id="element-4314" />
          Back to Bills
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-4315">
        <div className="space-y-6" data-id="element-4316">
          {/* Main Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4317">
            <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-4318">
              {isEditMode ? 'Edit Bill' : 'Create New Bill'}
            </h2>
            {/* Bill Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4319">
              <div data-id="element-4320">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4321">
                  Bill Number
                </label>
                <input type="text" value={formData.billNumber} onChange={e => setFormData({
                ...formData,
                billNumber: e.target.value
              })} placeholder="Auto-generated if left empty" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4322" />
              </div>
              <div data-id="element-4323">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4324">
                  Supplier *
                </label>
                <select value={formData.supplierId} onChange={e => {
                const supplier = suppliers.find(s => s.id === e.target.value);
                setFormData({
                  ...formData,
                  supplierId: e.target.value,
                  supplierName: supplier?.name || ''
                });
              }} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4325">
                  <option value="" data-id="element-4326">Select a supplier</option>
                  {suppliers.map(supplier => <option key={supplier.id} value={supplier.id} data-id="element-4327">
                      {supplier.name}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4328">
              <div data-id="element-4329">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4330">
                  Issue Date *
                </label>
                <input type="date" required value={formData.issueDate} onChange={e => setFormData({
                ...formData,
                issueDate: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4331" />
              </div>
              <div data-id="element-4332">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4333">
                  Due Date *
                </label>
                <input type="date" required value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4334" />
              </div>
            </div>
            <div className="mb-6" data-id="element-4335">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4336">
                Description
              </label>
              <input type="text" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Brief description of the bill" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4337" />
            </div>
          </div>
          {/* Bill Items */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4338">
            <div className="flex items-center justify-between mb-4" data-id="element-4339">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4340">Bill Items</h3>
              <button type="button" onClick={addItem} className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-4341">
                <PlusIcon size={18} data-id="element-4342" />
                Add Item
              </button>
            </div>
            <div className="space-y-4" data-id="element-4343">
              {items.map(item => <div key={item.id} className="grid grid-cols-12 gap-4 items-start" data-id="element-4344">
                  <div className="col-span-12 md:col-span-5" data-id="element-4345">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4346">
                      Description
                    </label>
                    <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Item description" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4347" />
                  </div>
                  <div className="col-span-4 md:col-span-2" data-id="element-4348">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4349">
                      Quantity
                    </label>
                    <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4350" />
                  </div>
                  <div className="col-span-4 md:col-span-2" data-id="element-4351">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4352">
                      Unit Price
                    </label>
                    <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4353" />
                  </div>
                  <div className="col-span-3 md:col-span-2" data-id="element-4354">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4355">
                      Total
                    </label>
                    <div className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900" data-id="element-4356">
                      MYR {item.total.toLocaleString('en-MY')}
                    </div>
                  </div>
                  <div className="col-span-1 flex items-end" data-id="element-4357">
                    <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4358">
                      <TrashIcon size={20} data-id="element-4359" />
                    </button>
                  </div>
                </div>)}
            </div>
            {/* Total */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4360">
              <div className="flex justify-end" data-id="element-4361">
                <div className="w-full max-w-xs space-y-2" data-id="element-4362">
                  <div className="flex justify-between py-2" data-id="element-4363">
                    <span className="text-sm text-gray-600" data-id="element-4364">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4365">
                      MYR {calculateSubtotal().toLocaleString('en-MY')}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-4366">
                    <span className="text-lg font-semibold text-gray-900" data-id="element-4367">
                      Total:
                    </span>
                    <span className="text-lg font-bold text-gray-900" data-id="element-4368">
                      MYR {calculateSubtotal().toLocaleString('en-MY')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Notes */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4369">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4370">
              Notes
            </label>
            <textarea value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={4} placeholder="Additional notes or payment terms..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4371" />
          </div>
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3" data-id="element-4372">
            <button type="button" onClick={() => navigate('/bills')} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4373">
              Cancel
            </button>
            <button type="button" onClick={handleSaveAsDraft} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4374">
              Save as Draft
            </button>
            <button type="submit" className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4375">
              <SaveIcon size={18} data-id="element-4376" />
              {isEditMode ? 'Update Bill' : 'Create Bill'}
            </button>
          </div>
        </div>
      </form>
    </div>;
}